/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactNode} from 'react';
import {Theme} from '@material-ui/core';
import Button, {ButtonProps} from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {Color} from '../../../types/types';

export interface TertiaryButtonProps extends ButtonProps {
  children: ReactNode;
  isAlt?: boolean;
  altColor?: Color;
  noUnderline?: boolean;
  noUnderlineOnFocus?: boolean;
  usecaactive?: boolean;
  disabled?: boolean,
  isRemoveTextpaddingRight?: boolean;
  fontSize?: string;
  uppercase?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
}
const useStyles = makeStyles<Theme, TertiaryButtonProps>(theme => ({
  container : {
    '&:focus-within' : {
      outline : `2px solid ${dlsColor('caBlue')}`
    }
  },
  root : {
    color         : ({isAlt, altColor}) => !isAlt ? dlsColor('aaBlue') : altColor || dlsColor('white'),
    fontWeight    : ({isAlt, altColor}) => !isAlt || altColor ? 'bold' : 400,
    fontSize      : ({fontSize}) => fontSize || theme.typography.fontSize,
    textTransform : ({uppercase}) => uppercase ? 'uppercase' : 'none',
    letterSpacing : dlsAlt('typography', 'btn', 'letterSpacing'),
    '&:hover'     : {
      textDecoration  : ({noUnderline}) => noUnderline ? 'none' : 'underline',
      backgroundColor : 'transparent'
    },
    '&:active' : {
      color : ({isAlt}) => dlsColor(`${!isAlt ? 'grey4' : 'white'}`)
    },
    '&:focus' : {
      color               : ({usecaactive}) => usecaactive ? dlsColor('caBlue') : dlsColor('grey1'),
      textDecoration      : ({noUnderlineOnFocus}) => noUnderlineOnFocus ? 'none' : 'underline',
      textDecorationColor : dlsColor('grey4')
    },
    '&:disabled' : {
      color : dlsAlt('components', 'btn', 'disabledColor')
    }
  },
  text : {
    paddingRight : ({isRemoveTextpaddingRight}) => isRemoveTextpaddingRight ? 0 : 'auto'
  }
}));

/**
 *
 * @TertiaryButton
 *
 * Tertiary buttons can be paired with primary buttons, secondary buttons,
 * or other tertiary buttons.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <TertiaryButton>Default</TertiaryButton>
 *       <TertiaryButton noUnderline>Default without underline</TertiaryButton>
 *       <br />
 *       <TertiaryButton disabled>Disabled</TertiaryButton>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
export const TertiaryButton = ({
  noUnderline = false,
  noUnderlineOnFocus = false,
  isRemoveTextpaddingRight = false,
  uppercase = true,
  usecaactive = false,
  ...props
}: TertiaryButtonProps) => {
  const classes = useStyles({
    ...props,
    uppercase,
    usecaactive,
    noUnderline,
    noUnderlineOnFocus,
    isRemoveTextpaddingRight
  });
  const pass = {...props};
  delete pass.isAlt;
  delete pass.altColor;
  return (
    <Button disableRipple disableFocusRipple
      classes={{
        root : classes.root,
        text : classes.text
      }}
      {...pass}>
      {props.children}
    </Button>
  );
};

export default TertiaryButton;
